import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Label } from '../ui/Label';
import { X, Download, Loader2, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { getSessoesByEvento, importarLocaisSessao, getEventos } from '../../services/api';
import { Skeleton } from '../ui/Skeleton';

function ImportLocaisSessaoModal({ isOpen, onClose, sessaoDestinoId, sessaoDestinoNome, onSuccess }) {
    const [eventos, setEventos] = useState([]);
    const [sessoes, setSessoes] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedSessaoOrigemId, setSelectedSessaoOrigemId] = useState('');
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    // 1. Fetch Events
    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true);
            try {
                const data = await getEventos();
                setEventos(data);
            } catch (error) {
                toast.error("Erro ao carregar eventos para importação.");
            } finally {
                setLoadingEvents(false);
            }
        };
        if (isOpen) {
            fetchEvents();
        }
    }, [isOpen]);

    // 2. Fetch Sessions when Event changes
    useEffect(() => {
        const fetchSessions = async () => {
            if (selectedEventId) {
                setLoadingSessions(true);
                try {
                    const data = await getSessoesByEvento(selectedEventId);
                    // Filtra a sessão de destino para não permitir importação para si mesma
                    const filteredSessions = data.filter(s => String(s.id) !== String(sessaoDestinoId));
                    setSessoes(filteredSessions);
                    setSelectedSessaoOrigemId('');
                } catch (error) {
                    toast.error("Erro ao carregar sessões de origem.");
                } finally {
                    setLoadingSessions(false);
                }
            } else {
                setSessoes([]);
                setSelectedSessaoOrigemId('');
            }
        };
        fetchSessions();
    }, [selectedEventId, sessaoDestinoId]);

    if (!isOpen) {
        return null;
    }

    const handleImport = async () => {
        if (!selectedSessaoOrigemId) {
            toast.warn('Por favor, selecione uma sessão de origem.');
            return;
        }

        setIsImporting(true);
        try {
            const response = await importarLocaisSessao(sessaoDestinoId, selectedSessaoOrigemId);
            toast.success(response.message);
            onSuccess();
        } catch (err) {
            console.error('Erro ao importar locais:', err);
            toast.error(err.message || 'Erro ao importar locais de sessão.');
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 z-[60] flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-title"
        >
            <Card className="w-full max-w-lg relative">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"
                    aria-label="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle id="import-title" className="flex items-center mb-2"><Copy className="mr-2 h-5 w-5" /> Importar Locais</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Copie os locais de outra sessão para a sessão atual: <span className="font-medium text-foreground">{sessaoDestinoNome}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-2">
                        <Label htmlFor="eventSelect" className="mb-1">1. Selecione o Evento de Origem</Label>
                        <Select onValueChange={setSelectedEventId} value={selectedEventId} disabled={loadingEvents || isImporting}>
                            <SelectTrigger id="eventSelect">
                                <SelectValue placeholder={loadingEvents ? "A carregar eventos..." : "Selecione um Evento"} />
                            </SelectTrigger>
                            <SelectContent>
                                {eventos.map(event => (
                                    <SelectItem key={event.id} value={String(event.id)}>{event.nome}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="sessionSelect" className="mb-1">2. Selecione a Sessão de Origem</Label>
                        <Select onValueChange={setSelectedSessaoOrigemId} value={selectedSessaoOrigemId} disabled={!selectedEventId || loadingSessions || isImporting || sessoes.length === 0}>
                            <SelectTrigger id="sessionSelect">
                                <SelectValue placeholder={loadingSessions ? "A carregar sessões..." : (sessoes.length === 0 ? "Nenhuma sessão disponível" : "Selecione uma Sessão")} />
                            </SelectTrigger>
                            <SelectContent>
                                {sessoes.map(session => (
                                    <SelectItem key={session.id} value={String(session.id)}>
                                        {session.nome}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedEventId && sessoes.length === 0 && !loadingSessions && (
                            <p className="text-xs text-muted-foreground mt-1">Nenhuma outra sessão encontrada neste evento.</p>
                        )}
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end gap-2 border-t pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isImporting}>
                        Cancelar
                    </Button>
                    <Button onClick={handleImport} disabled={isImporting || !selectedSessaoOrigemId}>
                        {isImporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
                        Importar Locais
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}

export default ImportLocaisSessaoModal;